// Pure authorization predicates + helpers for UserManagement (Phase 2).
//
// These were inline `disabled={...}` boolean expressions in the JSX — i.e.
// security-relevant rules (a regression here is a privilege-escalation bug, e.g.
// a manager deleting a super admin). Extracted so they are single-source and
// can be unit-tested as a truth table. `can*` returns whether the action is
// ALLOWED, so the JSX uses `disabled={!canX(actor, user)}`.

export type ActorContext = {
  isManager: boolean;
  isFirstSuperAdmin: boolean;
  currentUserId: string | null;
};

export type TargetUser = {
  id: string;
  is_super_admin?: boolean | null;
};

/** Block / unblock a user. Espelha as regras que a edge function `block-user`
 *  já aplica no servidor — antes a tela divergia dela nos dois sentidos: exibia
 *  o botão de bloquear a si mesmo (que a função sempre recusa com 403) e
 *  escondia o de bloquear outro super admin do Super Admin #1, que a função
 *  aceita. */
export function canBlockUser(actor: ActorContext, target: TargetUser): boolean {
  // Ninguém se auto-bloqueia: seria trancar a si mesmo pra fora.
  if (target.id === actor.currentUserId) return false;
  if (!target.is_super_admin) return true;
  // Alvo é super admin: gerente nunca, e super admin só se for o #1.
  return !actor.isManager && actor.isFirstSuperAdmin;
}

/** Reset a user's password. A manager can never reset a super admin's password. */
export function canResetPassword(actor: ActorContext, target: TargetUser): boolean {
  return !(target.is_super_admin && actor.isManager);
}

/** Delete a user. A super admin can only be deleted by themselves, unless the
 *  actor is the first super admin. */
export function canDeleteUser(actor: ActorContext, target: TargetUser): boolean {
  return !(target.is_super_admin && target.id !== actor.currentUserId && !actor.isFirstSuperAdmin);
}

/** Whether a user is currently blocked (banned_until in the future). `now` is
 *  injectable for testing. */
export function isUserBlocked(user: { banned_until?: string | null }, now: Date = new Date()): boolean {
  if (!user.banned_until) return false;
  return new Date(user.banned_until) > now;
}

/** Normalize a stored 12-month payment schedule (or fall back to the default)
 *  and report whether it differs from the default. */
export function resolveSchedule(
  raw: unknown,
  defaultSchedule: readonly number[],
): { schedule: number[]; differs: boolean } {
  const schedule =
    Array.isArray(raw) && raw.length === 12
      ? raw.map((n: unknown) => Number(n) || 0)
      : [...defaultSchedule];
  return { schedule, differs: schedule.some((v, i) => v !== defaultSchedule[i]) };
}
